<template>
  <div class="navigation-menu">
    <template v-if="userService.currentBusinessArea">
      <div class="px-1 py-2">
        <x-drop-down-button
          :items="availableBusinessAreasSelectItems"
          :dropdown-icon="!$vuetify.display.xs"
          button-min-width="100%"
          button-max-width="100%"
          @select="selectBusinessArea"
        >
          <template #default>
            <div class="text-h2 text-overflow">{{ userService.currentBusinessArea.name }}</div>
          </template>
          <template #append-list-item>
            <v-list-item
              class="px-2 py-1"
              :class="{ 'bg-beton4': !isPermissionsSatisfy([PermissionType.CreateBusinessArea]) }"
              base-color="blue"
              :disabled="!isPermissionsSatisfy([PermissionType.CreateBusinessArea])"
              @click="goToCreateBusinessAreaPage"
            >
              <v-list-item-title
                class="text-h4"
                :class="
                  isPermissionsSatisfy([PermissionType.CreateBusinessArea])
                    ? 'text-blue'
                    : 'text-beton'
                "
                v-text="'Создать бизнес-направление'"
              />
            </v-list-item>
          </template>
        </x-drop-down-button>
      </div>

      <v-divider />

      <div class="scroll-container">
        <div class="scroll-content">
          <v-list-subheader class="ml-2 text-h3">Управление</v-list-subheader>

          <div class="px-2">
            <template
              v-for="(item, index) in managementLinks"
              :key="index"
            >
              <v-list-item
                :title="item.title"
                :to="item.link"
                color="blue"
              />
            </template>
          </div>

          <v-divider />
          <v-list-subheader class="ml-2 text-h3">Настройка</v-list-subheader>

          <div class="px-2">
            <template
              v-for="(item, index) in settingsLinks"
              :key="index"
            >
              <v-list-item
                :title="item.title"
                :to="item.link"
                color="blue"
              />
            </template>
          </div>
        </div>
      </div>
    </template>

    <div
      v-else
      class="pa-2"
    >
      <v-btn
        variant="flat"
        color="blue"
        text="Создать бизнес-направление"
        prepend-icon="mdi-plus"
        block
        :disabled="!isPermissionsSatisfy([PermissionType.CreateBusinessArea])"
        @click="goToCreateBusinessAreaPage"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import { UserService } from '~/app-modules/user/services/user.service';
import { isPermissionsSatisfy } from '~/app-modules/auth/composables/useAccess';
import { PermissionType } from '~/app-modules/user-access-right/clients/enums/permission-type';
import { TItem } from '~/app-modules/ux-system/components/drop-down.vue';

defineOptions({
  name: 'AppLayoutNavigationMenu',
});

const userService = useIOC(UserService);
const { push } = useRouter();

const availableBusinessAreasSelectItems = computed(() =>
  userService.allAvailableBusinessAreas.map((b) => {
    return {
      label: b.name,
      value: b.id,
      callback: async () => await push('/business-area'),
    } as TItem;
  }),
);

async function selectBusinessArea(item: TItem) {
  const newBusinessArea = userService.allAvailableBusinessAreas.find((b) => b.id === item.value);

  if (!newBusinessArea) {
    throw new Error(`Business area not found; id: ${item.value};`);
  }
  userService.currentBusinessArea = newBusinessArea;
  await item.callback!();
}

function goToCreateBusinessAreaPage() {
  push('/business-area/create');
}

const managementLinks = computed(() => {
  if (!isPermissionsSatisfy([PermissionType.ViewBusinessAreaContent])) return [];

  const result = [
    { title: 'Услуги', link: '/provision' },
    { title: 'Владельцы кошельков', link: '/wallet-owner' },
    { title: 'Транзакции', link: '/transaction' },
    { title: 'Типы договоров', link: '/agreement-type' },
    { title: 'Счета на оплату', link: '/order' },
  ];

  if (userService.currentBusinessArea.enableAdvancedPriceManagement) {
    result.push({ title: 'Тарифы', link: '/price' });
    result.push({ title: 'Теги тарифов', link: '/price-tag' });
    result.push({ title: 'Задания по фиксации тарифа', link: '/fixing-price-operation' });
  }

  result.push({ title: 'Документы на реализацию', link: '/create-document-for-selling-operation' });
  result.push({ title: 'Выгрузки', link: '/export' });

  return result;
});

const settingsLinks = computed(() => {
  if (!isPermissionsSatisfy([PermissionType.ViewBusinessAreaContent]))
    return [{ title: 'Пользователи', link: '/user' }];

  return [
    { title: 'Пользователи', link: '/user' },
    { title: 'Аудит', link: '/audit-event' },
  ];
});
</script>

<style lang="scss">
.navigation-menu {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 56px);

  &.xs {
    height: fit-content;
  }
}

.scroll-container {
  flex: 1 1 auto;
  overflow: hidden;
}

.scroll-content {
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
}
</style>
